import { clamp } from '../game/util';

export function polyArea(pts){ let a=0; for (let i=0;i<pts.length;i++){ const p=pts[i], q=pts[(i+1)%pts.length]; a+=p[0]*q[1]-q[0]*p[1]; } return Math.abs(a)/2; }
export function chainWays(ways){ // ways: arrays of [x,z]; join by matching endpoints
  const pool = ways.map(w => w.slice()).filter(w => w.length>1); const out = [];
  const key = p => p[0].toFixed(2)+','+p[1].toFixed(2);
  while (pool.length){
    let cur = pool.shift(); let grew = true;
    while (grew){ grew = false;
      for (let i=0;i<pool.length;i++){ const w = pool[i];
        if (key(w[0])===key(cur.at(-1))){ cur = cur.concat(w.slice(1)); pool.splice(i,1); grew = true; break; }
        if (key(w.at(-1))===key(cur.at(-1))){ cur = cur.concat(w.slice(0,-1).reverse()); pool.splice(i,1); grew = true; break; }
        if (key(w.at(-1))===key(cur[0])){ cur = w.slice(0,-1).concat(cur); pool.splice(i,1); grew = true; break; }
        if (key(w[0])===key(cur[0])){ cur = w.slice(1).reverse().concat(cur); pool.splice(i,1); grew = true; break; }
      } }
    out.push(cur);
  }
  return out;
}
export function clipSegRect(a, b, R){ // Liang–Barsky, returns [p,q] or null
  let t0=0, t1=1; const dx=b[0]-a[0], dz=b[1]-a[1];
  for (const [p,q] of [[-dx, a[0]-R.x0],[dx, R.x1-a[0]],[-dz, a[1]-R.z0],[dz, R.z1-a[1]]]){
    if (p===0){ if (q<0) return null; continue; } const r = q/p; if (p<0){ if (r>t1) return null; if (r>t0) t0=r; } else { if (r<t0) return null; if (r<t1) t1=r; } }
  return [[a[0]+dx*t0, a[1]+dz*t0],[a[0]+dx*t1, a[1]+dz*t1]];
}
export function coastlineToSea(lines, half){
  // OSM coastline: land on the LEFT of direction of travel, sea on the right. Work in (x east, n north) = (x, -z).
  const R = {x0:-half, x1:half, z0:-half, z1:half}; const polys = [];
  const inside = p => p[0]>=R.x0-0.01 && p[0]<=R.x1+0.01 && p[1]>=R.z0-0.01 && p[1]<=R.z1+0.01;
  // perimeter param, clockwise in (x, n): top-left → top-right → bottom-right → bottom-left
  const P = 8*half;
  const tOf = p => { const x=p[0], n=-p[1]; const e = 0.05;
    if (Math.abs(n-half)<e) return x+half; if (Math.abs(x-half)<e) return 2*half + (half-n); if (Math.abs(n+half)<e) return 4*half + (half-x); return 6*half + (n+half); };
  const pOf = t => { t = ((t%P)+P)%P; if (t<2*half) return [t-half, -half]; if (t<4*half) return [half, -(half-(t-2*half))]; if (t<6*half) return [half-(t-4*half), half]; return [-half, -((t-6*half)-half)]; };
  for (const line of lines){
    // split into runs inside the rect
    let run = [];
    const flush = () => { if (run.length>=2) pieces.push(run); run = []; };
    const pieces = [];
    for (let i=0;i<line.length-1;i++){
      const c = clipSegRect(line[i], line[i+1], R); if (!c){ flush(); continue; }
      if (!run.length) run.push(c[0]); else if (Math.hypot(run.at(-1)[0]-c[0][0], run.at(-1)[1]-c[0][1])>0.05){ flush(); run.push(c[0]); }
      run.push(c[1]);
      if (!inside(line[i+1])) flush();
    }
    flush();
    for (const pc of pieces){
      const a = pc[0], b = pc.at(-1);
      const onEdge = p => Math.abs(Math.abs(p[0])-half)<0.05 || Math.abs(Math.abs(p[1])-half)<0.05;
      if (!onEdge(a) || !onEdge(b)) continue; // way ends inside the box: skipped (handled by chaining upstream)
      const poly = pc.slice(); let t = tOf(b); const tEnd = tOf(a);
      let dist = ((tEnd - t)%P + P)%P; // clockwise walk from b to a
      const cornersT = [0, 2*half, 4*half, 6*half];
      let guard = 0;
      while (guard++ < 8){ let next = null; for (const ct of cornersT){ const d = ((ct - t)%P + P)%P; if (d>0.01 && d < dist && (next===null || d < next)) next = d; }
        if (next===null) break; t += next; dist -= next; poly.push(pOf(t)); }
      polys.push(poly);
    }
  }
  return polys;
}
// 0..1 pseudo-random from an integer id (xorshift-ish mix), stable across runs and machines
export function hashFrac(id: number){ let x = (id | 0) ^ 0x9e3779b9; x = Math.imul(x ^ (x >>> 16), 0x85ebca6b); x = Math.imul(x ^ (x >>> 13), 0xc2b2ae35); x ^= x >>> 16; return (x >>> 0) / 4294967296; }
export function parseOSM(j, lat, lon, half){
  const nodes = new Map(), ways = new Map();
  for (const el of j.elements){ if (el.type==='node') nodes.set(el.id, el); else if (el.type==='way') ways.set(el.id, el); }
  const kx = 111320*Math.cos(lat*Math.PI/180), kz = 111320;
  const toPts = el => { const pts=[]; for (const id of el.nodes){ const n = nodes.get(id); if (n) pts.push([(n.lon-lon)*kx, -(n.lat-lat)*kz]); } return pts; };
  const closed = pts => { if (pts.length>1 && Math.hypot(pts[0][0]-pts.at(-1)[0], pts[0][1]-pts.at(-1)[1])<0.01) pts.pop(); return pts; };
  const out = {buildings:[], roads:[], parks:[], water:[], coast:[]};
  const usedInRel = new Set();
  for (const el of j.elements){
    if (el.type!=='relation' || !el.tags) continue;
    const isWater = el.tags.natural==='water' || el.tags.waterway==='riverbank' || el.tags.water;
    const isPark = el.tags.leisure==='park' || el.tags.landuse==='grass' || el.tags.leisure==='garden';
    if (!isWater && !isPark) continue;
    const outer = el.members.filter(m => m.type==='way' && (m.role==='outer' || m.role==='')).map(m => ways.get(m.ref)).filter(Boolean);
    for (const w of outer) usedInRel.add(w.id);
    const rings = chainWays(outer.map(toPts)).map(closed).filter(r => r.length>=3);
    (isWater ? out.water : out.parks).push(...rings);
  }
  for (const el of j.elements){
    if (el.type!=='way' || !el.tags) continue;
    if (el.tags.building && el.tags.building!=='no'){
      const pts = closed(toPts(el)); if (pts.length<3) continue;
      let h = parseFloat((el.tags.height||'').replace(/[^\d.]/g,''));
      if (!(h>0)){ const lv = parseFloat(el.tags['building:levels']); h = lv>0 ? lv*3.6 : 9 + hashFrac(el.id)*8; }   // deterministic: every client and the server must agree
      h = clamp(h, 4, 420); const area = polyArea(pts); if (area < 40) continue;
      out.buildings.push({id:String(el.id), pts, h, vol:area*h, tags:el.tags});
    } else if (el.tags.highway){ const pts = toPts(el); if (pts.length>=2) out.roads.push({pts, type:el.tags.highway}); }
    else if (el.tags.natural==='coastline'){ out.coast.push(toPts(el)); }
    else if (!usedInRel.has(el.id) && (el.tags.leisure==='park' || el.tags.landuse==='grass' || el.tags.leisure==='garden' || el.tags.leisure==='pitch')){ out.parks.push(closed(toPts(el))); }
    else if (!usedInRel.has(el.id) && (el.tags.natural==='water' || el.tags.waterway==='riverbank')){ out.water.push(closed(toPts(el))); }
  }
  if (out.coast.length){ const chains = chainWays(out.coast); out.water.push(...coastlineToSea(chains, half+150)); }
  return out;
}
// ---- IndexedDB cache for map JSON
